/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { isError } from "@epic-mw/error-types";
import { useMemo } from "react";
import { useLocation } from "@epic-mw/app-context";
import { useLocale } from "@epic-mw/i18n";
import { cleanForGame } from "./CosmosRichText.game";

const ensureQuotesInBlockquotes = (html?: string | null): string => {
	return (
		html?.toString().replace(/<blockquote>([\s\S]*?)<\/blockquote>/g, (_match, content: string) => {
			let trimmed = content?.trim() || "";
			if (!(trimmed.startsWith('"') || trimmed.startsWith("“"))) trimmed = `"${trimmed}`;
			if (!(trimmed.endsWith('"') || trimmed.endsWith("”"))) trimmed = `${trimmed}"`;
			return `<blockquote>${trimmed}</blockquote>`;
		}) || ""
	);
};

const localizeLinks = (locale: string, html?: string | null): string => {
	return (
		// catch any href values, contained in double or single quote
		html?.replace(/href=(["'])(\/[^"']+)\1/g, (match, quote, href) => {
			try {
				const tempBase = "https://base";
				const url = new URL(href, tempBase);

				// don't append lang param if locale path is already present
				const pathSegments = url.pathname.split("/").filter(Boolean);
				if (pathSegments.length > 0 && pathSegments[0] === locale) {
					return match;
				}

				// append lang param if not already present
				if (!url.searchParams.has("lang")) {
					url.searchParams.append("lang", locale);
				}
				const updatedHref = url.href.replace(tempBase, "");
				return `href=${quote}${updatedHref}${quote}`;
			} catch (error) {
				console.error("CosmosRichText - Error localizing relative href: ", href, error);
				return match;
			}
		}) || ""
	);
};

const ytDomainRegex = new RegExp('src="(http:|https:)?(//)?(www.)?youtube.com/embed/', "ig");
const ytNoCookieReplacement = 'src="https://www.youtube-nocookie.com/embed/';

/**
 * Within a HTML copy bloc, replace any youtube embed with the nocookie domain
 * @param copy
 * @returns
 */
export const youtubeEmbedSanitize = (copy: string): string => {
	return copy.replace(ytDomainRegex, ytNoCookieReplacement);
};

/**
 * Within a HTML copy block, replace any vimeo embed with the dnt param if it doesnt exist
 * @param copy
 * @returns
 */
export const vimeoEmbedSanitize = (copy: string): string => {
	let sanitized = copy || "";

	if (sanitized.indexOf("player.vimeo.com") > -1) {
		const vimeoDomainRegex = new RegExp('src="((http:|https:)?//player.vimeo.com/video/[a-z0-9?=&;_]+)"', "ig");

		let match = vimeoDomainRegex.exec(sanitized);
		while (match !== null) {
			if (match && match.length && match[1]) {
				const originalVimeoSrc = match[1].replace(/&amp;/gi, "&");
				try {
					const parsedUrl = new URL(originalVimeoSrc);
					if (parsedUrl && parsedUrl.searchParams.get("dnt") !== "true") {
						parsedUrl.searchParams.set("dnt", "true");
						sanitized = sanitized.replace(match[1], parsedUrl.toString());
						sanitized = sanitized.replace(/&amp%3B/gi, "&amp;");
					}
				} catch (e) {
					console.error("Failed to create URL from vimeosrc", isError(e) && e.message);
				}
			}
			match = vimeoDomainRegex.exec(sanitized);
		}
	}
	return sanitized;
};

/** Replace dashed unicode characters that don't work on mac
 *   ex: in‑game The dash is actually unicode 8209 and not a regular hyphen
 */
export const replaceUnicodeDash = (text: string): string => {
	let finalText = text;
	if (finalText && finalText.indexOf("‑") !== -1) {
		// replace char code 8209 with 45
		finalText = finalText.replace(/‑/g, "-");
	}
	return finalText;
};

/**
 * Replace any occurances of :locale and replace it will the current locale
 */
export const replaceDynamicLocale = (locale: string, copy: string): string => {
	let finalCopy = copy;
	// Replace any occurances of :locale and replace it will the current locale
	if (finalCopy && finalCopy.indexOf("/:locale") > -1) {
		finalCopy = finalCopy.replace(/\/:locale/gi, `/${locale}`);
	}
	if (finalCopy && finalCopy.indexOf("/{locale}") > -1) {
		finalCopy = finalCopy.replace(/\/\{locale\}/gi, `/${locale}`);
	}
	return finalCopy;
};

const formatHtml = (locale: string, location: URL, html?: string | null, options?: { gameMode?: boolean }): string => {
	if (!html) return "";

	html = youtubeEmbedSanitize(html);
	html = vimeoEmbedSanitize(html);
	html = replaceUnicodeDash(html);
	html = replaceDynamicLocale(locale, html);

	html = ensureQuotesInBlockquotes(html);

	if (locale) {
		html = localizeLinks(locale, html);
	}

	if (options?.gameMode) {
		html = cleanForGame(location, html);
	}

	return html;
};

export const useFormatHtml = (html?: string | null, options?: { gameMode?: boolean }): string => {
	const location = useLocation();
	const locale = useLocale();

	return useMemo(() => {
		return formatHtml(locale, location, html, options);
	}, [locale, location, html, options]);
};
